import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { DeckingItem, HardwareListGroup, HardwareListItem, PostBasesItem } from '../../types';
import type { HardwareItem } from '../../utils/materialsDatabaseService';
import {
  applyHardwareQtyCost,
  emptyHardwareListItem,
  HARDWARE_GROUP_LABEL,
  HARDWARE_TABLE_GROUPS,
  newHardwareListItemId,
  withHardwareLineTotal,
} from '../../utils/proposalHardwareCsvParser';
import { deckingItemTotal, deckingLinearFeet } from '../../utils/proposalStructureDecking';
import { postBasesItemTotal } from '../../utils/proposalStructurePostBases';
import {
  formatStructureLineCompareTitle,
  structureLineCompareRowClass,
  type StructureLineCompareMap,
} from '../../utils/structureLineCompare';
import './HardwareListTable.css';

export type HardwarePostBasesEditor = {
  rows: PostBasesItem[];
  products: HardwareItem[];
  onRemove: (id: string) => void;
  onProduct: (id: string, productId: string) => void;
  onQty: (id: string, qty: number) => void;
  onUnitCost?: (id: string, unitCost: number) => void;
};

export type HardwareDeckingEditor = {
  rows: DeckingItem[];
  products: HardwareItem[];
  productWidth: (productName: string | undefined, fallback?: number) => number;
  onRemove: (id: string) => void;
  onProduct: (id: string, productId: string) => void;
  onSqFt: (id: string, sqFt: number) => void;
  onPricePerLinFt?: (id: string, price: number) => void;
};

interface HardwareListTableProps {
  items: HardwareListItem[];
  disabled?: boolean;
  onChange: (items: HardwareListItem[]) => void;
  onBlur?: () => void;
  formatCurrency: (value: number) => string;
  postBases?: HardwarePostBasesEditor;
  decking?: HardwareDeckingEditor;
  compareMarks?: {
    hardware?: StructureLineCompareMap;
    decking?: StructureLineCompareMap;
    postBases?: StructureLineCompareMap;
  };
}

function initialGroupDraftIds(): Record<HardwareListGroup, string> {
  return {
    pegs: newHardwareListItemId(),
    steel: newHardwareListItemId(),
    fasteners: newHardwareListItemId(),
    other: newHardwareListItemId(),
  };
}

function defaultLumpName(group: HardwareListGroup): string {
  if (group === 'pegs') return 'Peg package';
  if (group === 'fasteners') return 'Fastener package';
  return '';
}

function isEmptyLumpRow(row: HardwareListItem, group: HardwareListGroup): boolean {
  if (group !== 'pegs' && group !== 'fasteners') return false;
  const noCost = !(Number(row.total) || Number(row.unitCost));
  const name = (row.name || '').trim();
  return noCost && (!name || name === defaultLumpName(group));
}

function applyLumpDefaultName(row: HardwareListItem, group: HardwareListGroup): HardwareListItem {
  if (
    (group === 'pegs' || group === 'fasteners') &&
    !(row.name || '').trim() &&
    (Number(row.total) || Number(row.unitCost))
  ) {
    return { ...row, name: defaultLumpName(group) };
  }
  return row;
}

function isBlankHardwareRow(row: HardwareListItem, group: HardwareListGroup): boolean {
  const noCost = !(Number(row.total) || Number(row.unitCost));
  const name = (row.name || '').trim();
  if (group === 'pegs' || group === 'fasteners') return isEmptyLumpRow(row, group);
  const noMaterial = !(row.material || '').trim() && !(row.section || '').trim();
  const noLength = !(row.length || '').trim();
  const noWeight = !(Number(row.weightLb) || 0);
  return noCost && !name && noMaterial && noLength && noWeight;
}

function isBlankPostBase(row: PostBasesItem): boolean {
  return !row.productName?.trim() && !(Number(row.quantity) > 0 && Number(row.unitCost) > 0);
}

function isBlankDecking(row: DeckingItem): boolean {
  return !row.productName?.trim() && !(Number(row.sqFt) > 0);
}

function withTrailingDraft<T>(
  rows: T[],
  disabled: boolean | undefined,
  isBlank: (row: T) => boolean,
  draft: T,
): T[] {
  if (disabled) return rows;
  const last = rows[rows.length - 1];
  if (last && isBlank(last)) return rows;
  return [...rows, draft];
}

const GroupTitle: React.FC<{ label: string; extra?: React.ReactNode }> = ({ label, extra }) => (
  <div className="hardware-group-title">
    <span>{label}</span>
    {extra}
  </div>
);

const ActionCell: React.FC<{ show: boolean; title: string; onClick: () => void }> = ({
  show,
  title,
  onClick,
}) => (
  <td className="hardware-col-action">
    {show && (
      <button type="button" className="timber-delete-btn" title={title} onClick={onClick}>
        <Trash2 size={12} />
      </button>
    )}
  </td>
);

const HardwareListTable: React.FC<HardwareListTableProps> = ({
  items,
  disabled,
  onChange,
  onBlur,
  formatCurrency,
  postBases,
  decking,
  compareMarks,
}) => {
  const [draftIds, setDraftIds] = useState(initialGroupDraftIds);
  const [postBasesDraftId, setPostBasesDraftId] = useState(newHardwareListItemId);
  const [deckingDraftId, setDeckingDraftId] = useState(newHardwareListItemId);

  const rotateGroupDraft = (group: HardwareListGroup) => {
    setDraftIds((prev) => ({ ...prev, [group]: newHardwareListItemId() }));
  };

  const commitNewItem = (group: HardwareListGroup, patch: Partial<HardwareListItem>) => {
    const row = emptyHardwareListItem(group);
    const next = applyLumpDefaultName(
      withHardwareLineTotal({ ...row, ...patch, id: draftIds[group] }),
      group,
    );
    onChange([...items, next]);
    rotateGroupDraft(group);
  };

  const updateItem = (id: string, group: HardwareListGroup, patch: Partial<HardwareListItem>) => {
    if (id === draftIds[group]) {
      commitNewItem(group, patch);
      return;
    }
    onChange(
      items.map((row) =>
        row.id === id ? applyLumpDefaultName(withHardwareLineTotal({ ...row, ...patch }), group) : row,
      ),
    );
  };

  const updateQtyCostLine = (
    id: string,
    group: HardwareListGroup,
    patch: { quantity?: number; cost?: number },
  ) => {
    const apply = (row: HardwareListItem): HardwareListItem =>
      applyHardwareQtyCost(row, {
        quantity: patch.quantity,
        unitCost: patch.cost,
      });
    if (id === draftIds[group]) {
      commitNewItem(group, apply(emptyHardwareListItem(group)));
      return;
    }
    onChange(items.map((row) => (row.id === id ? applyLumpDefaultName(apply(row), group) : row)));
  };

  const removeRow = (id: string) => {
    if (id === draftIds.pegs || id === draftIds.steel || id === draftIds.fasteners || id === draftIds.other) {
      return;
    }
    onChange(items.filter((row) => row.id !== id));
  };

  const editPostBasesDraft = (isDraft: boolean, fn: () => void) => {
    fn();
    if (isDraft) setPostBasesDraftId(newHardwareListItemId());
  };

  const editDeckingDraft = (isDraft: boolean, fn: () => void) => {
    fn();
    if (isDraft) setDeckingDraftId(newHardwareListItemId());
  };

  const postBasesDisplay = postBases
    ? withTrailingDraft(
        postBases.rows,
        disabled,
        isBlankPostBase,
        { id: postBasesDraftId, quantity: 0, unitCost: 0, total: 0 },
      )
    : [];

  const deckingDisplay = decking
    ? withTrailingDraft(
        decking.rows,
        disabled,
        isBlankDecking,
        { id: deckingDraftId, widthInches: 0, sqFt: 0, pricePerLinFt: 0, total: 0 },
      )
    : [];

  const readonly = disabled ? ' hardware-table--readonly' : '';

  return (
    <div className="timber-list-container hardware-list-wrap">
      {HARDWARE_TABLE_GROUPS.map((group) => {
        const rows = items.filter((row) => row.group === group);
        const label = HARDWARE_GROUP_LABEL[group];
        const lump = group === 'pegs' || group === 'fasteners';
        const draftRow = emptyHardwareListItem(group);
        draftRow.id = draftIds[group];
        draftRow.quantity = 0;
        const displayRows = withTrailingDraft(
          rows,
          disabled,
          (row) => isBlankHardwareRow(row, group),
          draftRow,
        );
        return (
          <div key={group} className="hardware-group" aria-label={label}>
            <GroupTitle label={label} />
            <table className={`hardware-table${lump ? ' hardware-table--qty-cost' : ' hardware-table--steel'}${readonly}`}>
              <thead>
                <tr>
                  <th className="hardware-col-name">Name</th>
                  <th className="hardware-col-material">{lump ? 'Material' : 'Material / section'}</th>
                  {!lump && <th className="hardware-col-length">Length</th>}
                  <th className="hardware-col-num">Qty</th>
                  {!lump && <th className="hardware-col-num">Weight</th>}
                  {!lump && <th className="hardware-col-num">$/ea</th>}
                  <th className="hardware-col-num">{lump ? 'Cost' : 'Total'}</th>
                  {lump && <th className="hardware-col-num">Total</th>}
                  <th className="hardware-col-action"></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((item) => {
                  const isDraft = item.id === draftIds[group];
                  const emptyLump = lump && isEmptyLumpRow(item, group);
                  const showAsDraft = isDraft || emptyLump;
                  const compareEntry = !showAsDraft ? compareMarks?.hardware?.[item.id] : undefined;
                  const compareClass = structureLineCompareRowClass(compareEntry, 'hardware');
                  return (
                    <tr
                      key={item.id}
                      className={[showAsDraft ? 'hardware-draft-row' : '', compareClass].filter(Boolean).join(' ') || undefined}
                      title={compareEntry ? formatStructureLineCompareTitle(compareEntry) : undefined}
                    >
                      <td className="hardware-col-name">
                        <input
                          type="text"
                          value={showAsDraft && item.name === defaultLumpName(group) ? '' : item.name}
                          disabled={disabled}
                          placeholder={showAsDraft ? 'Add item' : lump ? defaultLumpName(group) : 'Part name'}
                          title={item.sourceProductionNumber ? `Production #${item.sourceProductionNumber}` : item.name || undefined}
                          onChange={(e) => updateItem(item.id, group, { name: e.target.value })}
                          onBlur={onBlur}
                        />
                      </td>
                      <td className="hardware-col-material">
                        {lump ? (
                          <span className="hardware-ref-text" title={item.material || item.section || undefined}>
                            {item.material || item.section || (showAsDraft ? '' : '—')}
                          </span>
                        ) : (
                          <input
                            type="text"
                            value={item.material || item.section || ''}
                            disabled={disabled}
                            placeholder={isDraft ? '' : 'Steel / HSS…'}
                            onChange={(e) => updateItem(item.id, group, { material: e.target.value })}
                            onBlur={onBlur}
                          />
                        )}
                      </td>
                      {!lump && (
                        <td className="hardware-col-length">
                          <input
                            type="text"
                            value={item.length || ''}
                            disabled={disabled}
                            placeholder={isDraft ? '' : '8\'-3"'}
                            onChange={(e) => updateItem(item.id, group, { length: e.target.value })}
                            onBlur={onBlur}
                          />
                        </td>
                      )}
                      <td className="hardware-col-num">
                        <input
                          type="number"
                          min="0"
                          value={item.quantity || ''}
                          disabled={disabled}
                          onChange={(e) => {
                            const quantity = parseFloat(e.target.value) || 0;
                            if (lump) updateQtyCostLine(item.id, group, { quantity });
                            else updateItem(item.id, group, { quantity });
                          }}
                          onBlur={onBlur}
                        />
                      </td>
                      {!lump && (
                        <td className="hardware-col-num">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.weightLb || ''}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(item.id, group, { weightLb: parseFloat(e.target.value) || 0 })
                            }
                            onBlur={onBlur}
                          />
                        </td>
                      )}
                      {!lump && (
                        <td className="hardware-col-num">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitCost || ''}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(item.id, group, { unitCost: parseFloat(e.target.value) || 0 })
                            }
                            onBlur={onBlur}
                          />
                        </td>
                      )}
                      <td className="hardware-col-num">
                        {lump ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitCost || ''}
                            disabled={disabled}
                            onChange={(e) =>
                              updateQtyCostLine(item.id, group, { cost: parseFloat(e.target.value) || 0 })
                            }
                            onBlur={onBlur}
                          />
                        ) : (
                          <span>{isDraft && !item.total ? '' : formatCurrency(item.total)}</span>
                        )}
                      </td>
                      {lump && (
                        <td className="hardware-col-num">
                          <span>{showAsDraft && !item.total ? '' : formatCurrency(item.total)}</span>
                        </td>
                      )}
                      <ActionCell
                        show={!disabled && !showAsDraft}
                        title="Remove line"
                        onClick={() => removeRow(item.id)}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {postBases && (
        <div className="hardware-group">
          <GroupTitle label="Post Bases" />
          <table className={`hardware-table hardware-table--post-bases${readonly}`}>
            <thead>
              <tr>
                <th className="hardware-col-name">Product</th>
                <th className="hardware-col-num">Qty</th>
                <th className="hardware-col-num">Cost</th>
                <th className="hardware-col-num">Total</th>
                <th className="hardware-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {postBasesDisplay.map((row, index) => {
                const matched = postBases.products.find((p) => p.name === row.productName);
                const isDraft = row.id === postBasesDraftId;
                const compareEntry = !isDraft ? compareMarks?.postBases?.[row.id] : undefined;
                const compareClass = structureLineCompareRowClass(compareEntry, 'hardware');
                return (
                  <tr
                    key={row.id || index}
                    className={[isDraft ? 'hardware-draft-row' : '', compareClass].filter(Boolean).join(' ') || undefined}
                    title={compareEntry ? formatStructureLineCompareTitle(compareEntry) : undefined}
                  >
                    <td className="hardware-col-name">
                      <select
                        className="hardware-decking-select"
                        disabled={disabled}
                        value={matched?.id || (row.productName ? '__current__' : '')}
                        onChange={(e) =>
                          editPostBasesDraft(isDraft, () => postBases.onProduct(row.id, e.target.value))
                        }
                        onBlur={onBlur}
                      >
                        <option value="">{isDraft ? 'Add item' : '— Select post base —'}</option>
                        {row.productName && !matched && (
                          <option value="__current__">{row.productName}</option>
                        )}
                        {postBases.products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {formatCurrency(p.unitPrice)}
                            {p.unit ? `/${p.unit}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="hardware-col-num">
                      <input
                        type="number"
                        min="0"
                        value={row.quantity || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          editPostBasesDraft(isDraft, () =>
                            postBases.onQty(row.id, parseFloat(e.target.value) || 0),
                          )
                        }
                        onBlur={onBlur}
                      />
                    </td>
                    <td className="hardware-col-num">
                      {postBases.onUnitCost ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.unitCost || ''}
                          disabled={disabled}
                          onChange={(e) =>
                            editPostBasesDraft(isDraft, () =>
                              postBases.onUnitCost!(row.id, parseFloat(e.target.value) || 0),
                            )
                          }
                          onBlur={onBlur}
                        />
                      ) : (
                        <span>{row.unitCost ? formatCurrency(row.unitCost) : ''}</span>
                      )}
                    </td>
                    <td className="hardware-col-num">
                      <span>{isDraft && !postBasesItemTotal(row) ? '' : formatCurrency(postBasesItemTotal(row))}</span>
                    </td>
                    <ActionCell
                      show={!disabled && !isDraft}
                      title="Remove post base"
                      onClick={() => postBases.onRemove(row.id)}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {decking && (
        <div className="hardware-group">
          <GroupTitle label="Decking" />
          <table className={`hardware-table hardware-table--decking${readonly}`}>
            <thead>
              <tr>
                <th className="hardware-col-name">Product</th>
                <th className="hardware-col-num">Sq Ft</th>
                <th className="hardware-col-num">Lin Ft</th>
                <th className="hardware-col-num">$/lf</th>
                <th className="hardware-col-num">Total</th>
                <th className="hardware-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {deckingDisplay.map((row, index) => {
                const width = decking.productWidth(row.productName, row.widthInches);
                const sqFt = row.sqFt || 0;
                const linFt = deckingLinearFeet(sqFt, width);
                const isDraft = row.id === deckingDraftId;
                const lineTotal = deckingItemTotal({ ...row, widthInches: width });
                const compareEntry = !isDraft ? compareMarks?.decking?.[row.id] : undefined;
                const compareClass = structureLineCompareRowClass(compareEntry, 'hardware');
                return (
                  <tr
                    key={row.id || index}
                    className={[isDraft ? 'hardware-draft-row' : '', compareClass].filter(Boolean).join(' ') || undefined}
                    title={compareEntry ? formatStructureLineCompareTitle(compareEntry) : undefined}
                  >
                    <td className="hardware-col-name">
                      <select
                        className="hardware-decking-select"
                        disabled={disabled}
                        value={decking.products.find((p) => p.name === row.productName)?.id || ''}
                        onChange={(e) =>
                          editDeckingDraft(isDraft, () => decking.onProduct(row.id, e.target.value))
                        }
                        onBlur={onBlur}
                      >
                        <option value="">{isDraft ? 'Add item' : '— Select decking product —'}</option>
                        {decking.products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {formatCurrency(p.unitPrice)}/lf
                            {p.widthInches ? ` (${p.widthInches}" wide)` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="hardware-col-num">
                      <input
                        type="number"
                        min="0"
                        value={sqFt || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          editDeckingDraft(isDraft, () =>
                            decking.onSqFt(row.id, parseFloat(e.target.value) || 0),
                          )
                        }
                        onBlur={onBlur}
                      />
                    </td>
                    <td className="hardware-col-num">
                      <span>{sqFt > 0 && width > 0 ? linFt.toLocaleString() : ''}</span>
                    </td>
                    <td className="hardware-col-num">
                      {decking.onPricePerLinFt ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.pricePerLinFt || ''}
                          disabled={disabled}
                          onChange={(e) =>
                            editDeckingDraft(isDraft, () =>
                              decking.onPricePerLinFt!(row.id, parseFloat(e.target.value) || 0),
                            )
                          }
                        />
                      ) : (
                        <span>{row.pricePerLinFt ? formatCurrency(row.pricePerLinFt) : ''}</span>
                      )}
                    </td>
                    <td className="hardware-col-num">
                      <span>{isDraft && !lineTotal ? '' : formatCurrency(lineTotal)}</span>
                    </td>
                    <ActionCell
                      show={!disabled && !isDraft}
                      title="Remove decking size"
                      onClick={() => decking.onRemove(row.id)}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HardwareListTable;
